import { MutationCache, QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/services/http/api-error';

declare module '@tanstack/react-query' {
  /** Every request goes through the http client, which rejects only with ApiError. */
  interface Register {
    defaultError: ApiError;
    /** `invalidates: false` for a call that changes no records (sign-in, export ticket). */
    mutationMeta: { invalidates?: boolean };
  }
}

/** A 4xx will fail the same way again; only network and server errors are worth one retry. */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < 1;
}

/**
 * Data is never trusted as fresh: a query refetches whenever its screen mounts, the window
 * regains focus or the network returns (cached rows show meanwhile). And any successful change
 * marks every query stale, so counts that it moves elsewhere (the dashboard's red and green
 * animals, list totals) are right on the next look; queries on screen refetch at once.
 * Pick lists set their own longer staleTime.
 */
export function createQueryClient(): QueryClient {
  const client: QueryClient = new QueryClient({
    mutationCache: new MutationCache({
      onSuccess: (_data, _variables, _context, mutation) => {
        if (mutation.meta?.invalidates === false) return;
        void client.invalidateQueries();
      },
    }),
    defaultOptions: {
      queries: { staleTime: 0, retry: shouldRetry },
      mutations: { retry: false },
    },
  });
  return client;
}
